<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useChatPagesStore } from "@stores/chatPages";
  import { useAccountsStore } from "@stores/accounts";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { Pencil, Copy, Trash2, Palette } from "lucide-vue-next";
  import { useToastsStore } from "@stores/toastsStore";
  import moment from "moment";

  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { Button } from "@/components/ui/button";
  import {
    DataTable,
    DataTableActions,
    type DataTableColumn,
    type DataTableAction,
  } from "@/components/episto-ui/data-table";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import ChatPageFormDialog, { type ChatPageForm } from "./ChatPageFormDialog.vue";
  import ChatPageAccountsCell from "./ChatPageAccountsCell.vue";
  import type { ChatPage } from "@/types";

  const PAGE_SIZE = 10;

  const router = useRouter();
  const chatPagesStore = useChatPagesStore();
  const accountsStore = useAccountsStore();
  const toast = useToastsStore();
  const { isEpisto } = storeToRefs(useCurrentUserStore());
  const { chatPages, chatPagesCount, currentPage, searchQuery, sortColumn, sortDirection } =
    storeToRefs(chatPagesStore);
  const { accounts, availableAccountsOptions } = storeToRefs(accountsStore);

  const totalPages = computed(() => Math.ceil(chatPagesCount.value / PAGE_SIZE) || 1);

  // Mono-account clients never choose where their page belongs, so the column
  // and the picker would both be noise.
  const isMultiAccount = computed(() => availableAccountsOptions.value.length > 1);
  const showAccounts = computed(() => isEpisto.value || isMultiAccount.value);

  const handleSort = (column: string) => {
    if (sortColumn.value === column) {
      sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
    } else {
      sortColumn.value = column;
      sortDirection.value = "asc";
    }
    chatPagesStore.fetchChatPages(1);
  };

  const handlePageChange = (page: number) => chatPagesStore.fetchChatPages(page);

  const isDialogOpen = ref(false);
  const isEditing = ref(false);
  const emptyForm = (): ChatPageForm => ({
    external_id: "",
    name: "",
    image_url: "",
    account_ids: isMultiAccount.value ? [] : availableAccountsOptions.value.map((option) => option.value),
    default_account_ids: [],
  });
  const currentChatPageData = ref<ChatPageForm>(emptyForm());

  onMounted(async () => {
    await accountsStore.fetch();
    chatPagesStore.fetchChatPages(1);
  });
  onUnmounted(() => chatPagesStore.resetAllFilters());

  const columns = computed<DataTableColumn<ChatPage>[]>(() => [
    { key: "image_url", label: "Logo" },
    ...(isEpisto.value
      ? [{ key: "external_id", label: "External ID", sortable: true, class: "font-mono text-xs" }]
      : []),
    { key: "name", label: "Name", sortable: true, class: "font-medium" },
    ...(showAccounts.value ? [{ key: "accounts", label: "Accounts" }] : []),
    { key: "created_at", label: "Created At", sortable: true },
    { key: "customize", label: "Theme" },
  ]);

  const editAction: DataTableAction = { key: "edit", label: "Edit", icon: Pencil };
  const duplicateAction: DataTableAction = { key: "duplicate", label: "Duplicate", icon: Copy };
  const deleteAction: DataTableAction = {
    key: "delete",
    label: "Delete",
    icon: Trash2,
    variant: "destructive",
    separatorBefore: true,
  };

  const actionsFor = (chatPage: ChatPage): DataTableAction[] => {
    const actions: DataTableAction[] = [];
    if (chatPage.can_update) actions.push(editAction);
    // A copy inherits the source accounts, and a page without any is rejected —
    // that is the Episto default page, which is never duplicated.
    if (chatPage.can_update && chatPage.accounts?.length) actions.push(duplicateAction);
    if (chatPage.can_delete) actions.push(deleteAction);
    return actions;
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return moment(date).format("YYYY-MM-DD");
  };

  const openCustomizer = (chatPage: ChatPage) =>
    router.push({ name: "ChatPageCustomizer", params: { token: chatPage.token } });

  const openCreateDialog = () => {
    isEditing.value = false;
    currentChatPageData.value = emptyForm();
    isDialogOpen.value = true;
  };

  const openEditDialog = (chatPage: ChatPage) => {
    isEditing.value = true;
    currentChatPageData.value = {
      ...chatPage,
      account_ids: (chatPage.accounts ?? []).map((account) => account.id),
      default_account_ids: (chatPage.accounts ?? [])
        .filter((account) => account.is_default)
        .map((account) => account.id),
    };
    isDialogOpen.value = true;
  };

  // Accounts carry which page is their default, and that is what locks the toggle in
  // the dialog — so any save or delete that can move a default must refetch them too.
  const refreshChatPagesAndAccounts = () =>
    Promise.all([chatPagesStore.fetchChatPages(currentPage.value), accountsStore.fetch()]);

  const saveChatPage = async () => {
    if (!currentChatPageData.value.account_ids?.length) {
      toast.error("Select at least one account");
      return;
    }

    try {
      const payload = {
        name: currentChatPageData.value.name,
        image_url: currentChatPageData.value.image_url,
        account_ids: currentChatPageData.value.account_ids ?? [],
        default_account_ids: (currentChatPageData.value.default_account_ids ?? []).filter((id) =>
          (currentChatPageData.value.account_ids ?? []).includes(id),
        ),
        ...(isEpisto.value && isEditing.value ? { external_id: currentChatPageData.value.external_id } : {}),
      };

      if (isEditing.value && currentChatPageData.value.id) {
        await chatPagesStore.updateChatPage(currentChatPageData.value.id, payload);
        toast.success("Chat page updated successfully");
      } else {
        await chatPagesStore.createChatPage(payload);
        toast.success("Chat page created. Open Customize to style it.");
      }
      await refreshChatPagesAndAccounts();
      isDialogOpen.value = false;
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }
  };

  // A duplicate carries the logo and the theme over, so the client starts from
  // their own branding rather than the Episto default.
  const duplicateChatPage = async (chatPage: ChatPage) => {
    try {
      await chatPagesStore.createChatPage({
        name: `Copy of ${chatPage.name}`,
        image_url: chatPage.image_url,
        customisation: chatPage.customisation,
        account_ids: (chatPage.accounts ?? []).map((account) => account.id),
      });
      await chatPagesStore.fetchChatPages(currentPage.value);
      toast.success("Chat page duplicated");
    } catch (error) {
      console.error(error);
      toast.error("Failed to duplicate chat page");
    }
  };

  const {
    isOpen: isDeleteAlertOpen,
    target: chatPageToDelete,
    requestDelete: confirmDelete,
    confirm: handleDelete,
  } = useConfirmDelete<ChatPage>(async (chatPage) => {
    try {
      await chatPagesStore.deleteChatPage(chatPage.id);
      await refreshChatPagesAndAccounts();
      toast.success("Chat page deleted successfully");
    } catch {
      toast.error("Failed to delete chat page");
    }
  });

  const handleAction = (action: string, chatPage: ChatPage) => {
    if (action === "edit") {
      openEditDialog(chatPage);
    } else if (action === "duplicate") {
      duplicateChatPage(chatPage);
    } else if (action === "delete") {
      confirmDelete(chatPage);
    }
  };
</script>

<template>
  <IndexPageLayout
    title="Chat pages"
    action-label="Add Chat Page"
    @action="openCreateDialog">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search chat pages..." />
    </template>

    <DataTable
      :items="chatPages"
      :columns="columns"
      :current-page="currentPage"
      :total-pages="totalPages"
      :sort-column="sortColumn"
      :sort-direction="sortDirection"
      empty-message="No chat pages yet. Create one to brand your questionnaires."
      @sort="handleSort"
      @page-change="handlePageChange">
      <template #cell-image_url="{ item }">
        <img
          v-if="item.image_url"
          :src="item.image_url"
          :alt="item.name"
          class="h-8 max-w-[110px] rounded object-contain" />
        <span v-else>-</span>
      </template>
      <template #cell-accounts="{ item }">
        <ChatPageAccountsCell :accounts="item.accounts" />
      </template>
      <template #cell-created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>
      <template #cell-customize="{ item }">
        <Button
          v-if="item.can_update"
          variant="outline"
          size="sm"
          class="text-xs"
          @click="openCustomizer(item)">
          <Palette class="mr-1.5 h-3.5 w-3.5" />
          Customize
        </Button>
      </template>
      <template #actions="{ item }">
        <DataTableActions
          v-if="actionsFor(item).length"
          :actions="actionsFor(item)"
          @action="(action) => handleAction(action, item)" />
      </template>
    </DataTable>

    <template #dialogs>
      <ChatPageFormDialog
        v-model:chat-page="currentChatPageData"
        :open="isDialogOpen"
        :is-editing="isEditing"
        :available-accounts="availableAccountsOptions"
        :accounts="accounts"
        :show-external-id="isEpisto && isEditing"
        :accounts-locked="!isEpisto && !isMultiAccount"
        @update:open="isDialogOpen = $event"
        @save="saveChatPage" />

      <ConfirmDialog
        v-model:open="isDeleteAlertOpen"
        title="Are you absolutely sure?"
        confirm-label="Delete"
        @confirm="handleDelete">
        This will delete the chat page
        <span class="font-bold">{{ chatPageToDelete?.name }}</span>
        and move its respondents to the default page. Links already sent fall back to the default page's branding.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
